/**
 * WorkItemQuickAddRow
 * ------------------------------------------------------------------
 * 実施業務テーブル最下部の「常設の空行」。
 * 設計: docs/design/work-item-picker-ux.md §6.2
 *
 *   業務名（インクリメンタル検索）で Enter → 金額へフォーカス
 *   金額で Enter → 行が追加され、また業務名へフォーカス
 *
 * 業種・カテゴリ・税区分・源泉・立替はユーザーに聞かない（D8）。
 * マスタ候補由来の値、無ければ既定値を使い、行のバッジで見せて後から変更できる。
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatJPY } from "@juro/shared";
import type { WorkItemMaster } from "../../api/workItemMasterApi.js";
import type { WorkItemInput } from "../../api/workItemsApi.js";
import { WorkItemCombobox, type ComboboxOption, type WorkItemComboboxHandle } from "../ui/WorkItemCombobox.js";
import { masterItemAmount, singleItemOf } from "./masterOptions.js";
import { useApplyMaster } from "./useApplyMaster.js";
import { toast } from "../Toast.js";
import {
  PROFESSION_LABEL, PROFESSION_COLOR,
  getDefaultTaxCategory, getDefaultIsPassThrough, getDefaultWithholdingTarget,
} from "../../lib/workItemConstants.js";

const PROFESSIONS = ["judicial", "administrative", "surveyor", "common", "other"] as const;

type Draft = {
  masterId?:      string;
  /** マスタ明細の金額（行の合計額） */
  masterAmount?:  number;
  /** マスタ明細の数量。金額を打ち替えても潰さない */
  masterQuantity?: number;
  title:          string;
  profession:     string;
  category:       string;
  tax_category:   string;
  withholding_target: boolean;
  is_pass_through:    boolean;
};

type Props = {
  caseId:    string;
  masters:   WorkItemMaster[];
  options:   ComboboxOption[];
  /** 案件で直近に使った業種（自由記述の新規行の既定値） */
  defaultProfession: string;
  onAdd:     (item: WorkItemInput) => void;
};

export function WorkItemQuickAddRow({
  caseId, masters, options, defaultProfession, onAdd,
}: Props) {
  const [draft, setDraft]   = useState<Draft | null>(null);
  const [amount, setAmount] = useState("0");
  const [profOverride, setProfOverride] = useState<string | null>(null);

  const comboRef  = useRef<WorkItemComboboxHandle>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  const effProfession = draft?.profession ?? profOverride ?? defaultProfession;
  const applyMut = useApplyMaster(caseId);

  // 業務名が確定したら金額セルへフォーカスを送る
  useEffect(() => {
    if (draft) {
      amountRef.current?.focus();
      amountRef.current?.select();
    }
  }, [draft]);

  function backToCombobox() {
    setDraft(null);
    setAmount("0");
    // 描画後にフォーカスを戻す
    setTimeout(() => comboRef.current?.focus(), 0);
  }

  function handleSelect(opt: ComboboxOption) {
    const master = masters.find((m) => m.id === opt.id);
    if (!master) return;

    const single = singleItemOf(master);
    if (!single) {
      // 束はその場で全行を展開して追加し、続けて次の入力へ
      applyMut.mutate(
        { masterId: master.id },
        { onSuccess: () => toast(`${master.name} の ${master.default_items.length} 行を追加しました`) },
      );
      setTimeout(() => comboRef.current?.focus(), 0);
      return;
    }

    const profession = single.profession ?? master.profession;
    const category   = single.category ?? "fee";
    const price      = masterItemAmount(single);
    setAmount(String(price));
    setDraft({
      masterId:       master.id,
      masterAmount:   price,
      masterQuantity: single.quantity ?? 1,
      title:          single.title,
      profession,
      category,
      tax_category:       single.tax_category       ?? getDefaultTaxCategory(category),
      withholding_target: single.withholding_target ?? getDefaultWithholdingTarget(category, profession),
      is_pass_through:    single.is_pass_through    ?? getDefaultIsPassThrough(category),
    });
  }

  function handleFreeText(text: string) {
    const profession = effProfession;
    const category   = "fee";
    setAmount("0");
    setDraft({
      title: text,
      profession,
      category,
      tax_category:       getDefaultTaxCategory(category),
      withholding_target: getDefaultWithholdingTarget(category, profession),
      is_pass_through:    getDefaultIsPassThrough(category),
    });
  }

  function commit() {
    if (!draft) return;
    const amt = Math.max(0, Math.floor(Number(amount) || 0));

    if (draft.masterId) {
      // 失敗時は入力内容を残す（金額の打ち替えが保存できていないことに気づけるように）
      applyMut.mutate(
        {
          masterId:       draft.masterId,
          amountOverride: amt !== draft.masterAmount ? amt : undefined,
          quantity:       draft.masterQuantity,
        },
        { onSuccess: () => backToCombobox() },
      );
      return;
    }

    onAdd({
      profession:         draft.profession,
      category:           draft.category,
      title:              draft.title,
      quantity:           1,
      unit_price:         amt,
      amount:             amt,
      tax_category:       draft.tax_category,
      withholding_target: draft.withholding_target,
      is_pass_through:    draft.is_pass_through,
    });
    backToCombobox();
  }

  function handleAmountKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      backToCombobox();
    }
  }

  const color = PROFESSION_COLOR[effProfession] ?? "#475569";

  const cellStyle: React.CSSProperties = {
    padding: "6px 8px", fontSize: 12, verticalAlign: "middle",
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      border: "1px dashed #cbd5e1", borderRadius: 8,
      background: draft ? "#fafffe" : "#fff",
      padding: "6px 10px", marginTop: 8,
    }}>
      <span style={{ color: "#94a3b8", fontSize: 13, width: 14, textAlign: "center" }}>＋</span>

      {/* 業種（選ばせない。既定値をバッジで見せ、必要なときだけ変えられる） */}
      {draft?.masterId ? (
        <span style={{
          background: `${color}18`, color, borderRadius: 99,
          padding: "2px 9px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
        }}>
          {PROFESSION_LABEL[effProfession] ?? effProfession}
        </span>
      ) : (
        <select
          value={effProfession}
          onChange={(e) => {
            setProfOverride(e.target.value);
            if (draft) setDraft({ ...draft, profession: e.target.value });
          }}
          title="業種（既定値。行を追加した後でも変更できます）"
          style={{
            background: `${color}12`, color, border: `1px solid ${color}33`,
            borderRadius: 99, padding: "2px 6px", fontSize: 11, fontWeight: 700,
            fontFamily: "inherit", cursor: "pointer",
          }}
        >
          {PROFESSIONS.map((p) => (
            <option key={p} value={p}>{PROFESSION_LABEL[p]}</option>
          ))}
        </select>
      )}

      {/* 業務名（インクリメンタル検索） */}
      <div style={{ ...cellStyle, flex: 1, minWidth: 0, padding: 0 }}>
        {draft ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{draft.title}</span>
            <button
              type="button"
              onClick={backToCombobox}
              title="やり直す（Esc）"
              style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 13 }}
            >
              ×
            </button>
          </div>
        ) : (
          <WorkItemCombobox
            ref={comboRef}
            options={options}
            onSelect={handleSelect}
            allowFreeText
            onFreeTextCommit={handleFreeText}
            placeholder="業務名を入力（例: 登記事項証明書）→ Enter"
            aria-label="業務名"
            emptyMessage="候補がありません。そのまま Enter で自由入力できます"
            footer={
              <span style={{ color: "#64748b" }}>
                候補は業務マスタから。{" "}
                <Link to="/settings/work-items" style={{ color: "#0f766e", fontWeight: 700 }}>
                  業務マスタを管理
                </Link>
              </span>
            }
          />
        )}
      </div>

      {/* 金額 */}
      <input
        ref={amountRef}
        type="number"
        min={0}
        inputMode="numeric"
        disabled={!draft}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        onKeyDown={handleAmountKeyDown}
        placeholder="金額"
        aria-label="金額"
        style={{
          width: 116, textAlign: "right", fontFamily: "inherit",
          padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13,
          background: draft ? "#fff" : "#f8fafc",
        }}
      />

      <button
        type="button"
        onClick={commit}
        disabled={!draft}
        style={{
          minHeight: 28, padding: "4px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700,
          fontFamily: "inherit",
          background: draft ? "#0f766e" : "#f1f5f9",
          color: draft ? "#fff" : "#94a3b8",
          border: `1px solid ${draft ? "#0f766e" : "#e2e8f0"}`,
          cursor: draft ? "pointer" : "not-allowed",
        }}
      >
        追加
      </button>

      {draft?.masterAmount !== undefined && Number(amount) !== draft.masterAmount && (
        <span style={{ fontSize: 11, color: "#c2410c", whiteSpace: "nowrap" }}>
          マスタ {formatJPY(draft.masterAmount)} から変更
        </span>
      )}
    </div>
  );
}
